/**
 * Minimal className utility (no external deps).
 *
 * Why:
 * - The UI package is consumed by a Next.js app via workspace/tsconfig path mapping.
 * - Depending on monorepo install layout, resolving "clsx" / "tailwind-merge" from this package
 *   can be brittle.
 *
 * Trade-off:
 * - This does NOT implement Tailwind conflict resolution like tailwind-merge.
 * - It does provide stable concatenation and object/array support like clsx.
 */

export type ClassDictionary = Record<string, boolean | null | undefined>;
export type ClassArray = ClassValue[];
export type ClassValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | ClassDictionary
  | ClassArray;

function toClassName(value: ClassValue): string {
  if (!value) return "";

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(toClassName).filter(Boolean).join(" ");
  }

  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([k]) => k)
      .join(" ");
  }

  // boolean true => ignore (keeps API compatible with clsx usage patterns)
  return "";
}

/**
 * Primary className helper.
 */
export function cx(...values: ClassValue[]): string {
  return values.map(toClassName).filter(Boolean).join(" ").trim();
}

/**
 * Alias kept for compatibility with some codebases.
 */
export const cn = cx;

/**
 * Utility for deterministic snapshots/tests: sorts whitespace-separated classes.
 * Note: this is purely a helper and does not preserve semantic ordering.
 */
export function sortCx(input: string): string {
  return String(input ?? "")
    .split(/\s+/g)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .join(" ");
}
